import React from 'react';
import { Typography } from '@sm/wds-react';

import {
  ComparisonTable,
  ComparisonTableHeader,
  ComparisonTableHeaderCell,
  ComparisonTableCategory,
  ComparisonTableFeatureRow,
  ComparisonTableFeatureCell,
  ComparisonTableFooter,
  ComparisonTableFooterCell,
} from '~pricing/components/ComparisonTable';
import { PackageListPropTypes, SummaryPackagesConfigPropTypes, ComparisonTablePropTypes } from '~pricing/lib/proptypes';

import SummaryComparisonTableHeader from './SummaryComparisonTableHeader';
import SummaryComparisonTableFooter from './SummaryComparisonTableFooter';

// Helper methods for generating a11y ids for the table headers
const createPackageA11yId = pricingPackage => `ctPkg-${pricingPackage.package.replace(/\s/g, '').toLowerCase()}`;
const createCategoryA11yId = category => `ctCat-${category.title.replace(/\s/g, '').toLowerCase()}`;
const createFeatureA11yId = feature => `ctFeat-${feature.title.replace(/\s/g, '').toLowerCase()}`;

const SummaryComparisonTable = ({ packages, packagesConfig, comparisonTableDef }) => (
  <div data-testid="SummaryComparisonTable">
    <Typography component="h2" variant="sectionTitle" className="wds-type--center wds-m-b-6">
      Feature and plan comparisons
    </Typography>
    <ComparisonTable packageCount={packages.length} className="sm-comparison-table--summary">
      <ComparisonTableHeader>
        <ComparisonTableHeaderCell aria-hidden="true" a11yId="ctTableHead">
          <Typography variant="cardTitle" component="span" aria-hidden="true">
            Our plans
          </Typography>
          {/*
            We have a different label for screen readers because "our features" makes more sense
            when having a screen reader read the feature cell than "our plans".
          */}
          <span className="sm-comparison-table__sr-text">Our features</span>
        </ComparisonTableHeaderCell>
        {packages.map(pricingPackage => (
          <SummaryComparisonTableHeader
            pricingPackage={pricingPackage}
            packageTableDetails={packagesConfig[pricingPackage.package].comparison}
            key={`comparison-header-${pricingPackage.details.label}`}
            a11yId={createPackageA11yId(pricingPackage)}
          />
        ))}
      </ComparisonTableHeader>
      {comparisonTableDef.map((category, index) => {
        const categoryA11yId = createCategoryA11yId(category);

        return (
          <ComparisonTableCategory
            title={category.title}
            packages={packages}
            opened={index === 0}
            key={`comparison-category-${category.title}`}
            a11yId={categoryA11yId}
          >
            {Object.keys(category.features).map(feature => {
              const featureA11yId = createFeatureA11yId(category.features[feature]);

              return (
                <ComparisonTableFeatureRow
                  title={category.features[feature].title}
                  tooltip={category.features[feature].tooltip}
                  key={`category-feature-${feature}`}
                  a11yId={featureA11yId}
                  a11yHeaders={['ctTableHead', categoryA11yId]}
                >
                  {packages.map(pricingPackage => (
                    <ComparisonTableFeatureCell
                      key={`package-feature-${feature}-${pricingPackage.details.label}`}
                      checked={packagesConfig[pricingPackage.package].comparison.features[feature]}
                      pricingPackage={pricingPackage}
                      a11yHeaders={[featureA11yId, createPackageA11yId(pricingPackage)]}
                    />
                  ))}
                </ComparisonTableFeatureRow>
              );
            })}
          </ComparisonTableCategory>
        );
      })}
      <ComparisonTableFooter>
        <ComparisonTableFooterCell>&nbsp;</ComparisonTableFooterCell>
        {packages.map(pricingPackage => (
          <SummaryComparisonTableFooter
            pricingPackage={pricingPackage}
            packageTableDetails={packagesConfig[pricingPackage.package].comparison}
            key={`comparison-footer-${pricingPackage.details.label}`}
          />
        ))}
      </ComparisonTableFooter>
    </ComparisonTable>
  </div>
);

SummaryComparisonTable.propTypes = {
  packages: PackageListPropTypes.isRequired,
  packagesConfig: SummaryPackagesConfigPropTypes.isRequired,
  comparisonTableDef: ComparisonTablePropTypes.isRequired,
};

export default SummaryComparisonTable;
